/**
 * A textarea you can name things from (RFC 0292 S3).
 *
 * The `@` picker for the two host-realm comment composers: the side column's
 * reply box and the document editor's popover. It owns the dropdown and the
 * keys, and nothing else — the draft lives in the caller, because the caller is
 * the one that knows what to do with the chips when the comment is sent.
 *
 * CANDIDATES ARE PASSED IN, not read here. `projectCandidates` needs the
 * project document, and a component that reached for it would work in these
 * two surfaces and be unusable from the third (the review app, behind the
 * bridge, with no project handle at all). Handing in a function keeps the one
 * ranked answer shared and the plumbing local.
 */

import type { Candidate } from '@mod/core/model'
import { type KeyboardEvent, useCallback, useRef, useState } from 'react'
import { SessionMark } from '../../services/sessions/SessionMark'
import {
  type ActiveQuery,
  activeMentionQuery,
  applyCandidate,
  type MentionDraft,
  reanchorMentions,
} from './mentionDraft'

export function MentionComposer({
  draft,
  onDraftChange,
  onSubmit,
  candidates,
  placeholder,
  rows = 1,
  disabled,
  autoFocus,
  onEscape,
  className,
}: {
  draft: MentionDraft
  onDraftChange: (next: MentionDraft) => void
  /** Enter, when the picker is closed. Shift-Enter always breaks the line. */
  onSubmit: () => void
  candidates: (query: string) => Candidate[]
  placeholder?: string
  rows?: number
  disabled?: boolean
  autoFocus?: boolean
  onEscape?: () => void
  className?: string
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const [active, setActive] = useState<ActiveQuery | null>(null)
  const [highlight, setHighlight] = useState(0)

  const open = active !== null
  const matches = open ? candidates(active.query) : []

  const focusOnMount = useCallback(
    (el: HTMLTextAreaElement | null) => {
      ref.current = el
      if (autoFocus) el?.focus()
    },
    [autoFocus],
  )

  const readCaret = (el: HTMLTextAreaElement, text: string) => {
    const next = activeMentionQuery(text, el.selectionStart ?? text.length)
    setActive(next)
    setHighlight(0)
  }

  const choose = (candidate: Candidate) => {
    if (!active) return
    const applied = applyCandidate(draft, active, candidate)
    onDraftChange(applied.draft)
    setActive(null)
    // The caret goes after the inserted chip, on the next tick: React has not
    // written the new value into the textarea yet, and setting selectionStart
    // against the old text puts the cursor in the middle of somebody's name.
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      el.setSelectionRange(applied.caret, applied.caret)
    })
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open && matches.length > 0) {
      // The picker OWNS these keys while it is open. Enter choosing a name
      // rather than sending is the whole reason: a person who has just typed
      // three letters of a session title has not finished their sentence.
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlight((h) => (h + 1) % matches.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlight((h) => (h - 1 + matches.length) % matches.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const picked = matches[highlight] ?? matches[0]
        if (picked) choose(picked)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        // Closes the PICKER, not the composer: the person is dismissing a
        // dropdown they opened by typing, not abandoning what they wrote.
        setActive(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
      return
    }
    if (e.key === 'Escape') onEscape?.()
  }

  return (
    <div className="relative flex-1">
      <textarea
        ref={focusOnMount}
        value={draft.text}
        onChange={(e) => {
          const text = e.target.value
          // Chips are re-anchored on every edit, so typing above one keeps it
          // pointing at the words it named. What cannot be re-anchored is
          // dropped here rather than at send, so the composer never shows a
          // chip it is not going to write.
          onDraftChange({
            text,
            mentions: reanchorMentions(text, draft.mentions),
          })
          readCaret(e.target, text)
        }}
        onKeyUp={(e) => readCaret(e.currentTarget, e.currentTarget.value)}
        onClick={(e) => readCaret(e.currentTarget, e.currentTarget.value)}
        onBlur={() => {
          // Left open across a blur the dropdown would sit over whatever the
          // person clicked next. A click ON a row is handled by onMouseDown,
          // which fires first.
          setActive(null)
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={
          className ??
          'min-h-[24px] w-full resize-y rounded border border-border/60 bg-background px-1.5 py-1 text-[12px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-border'
        }
      />
      {open && matches.length > 0 && (
        <ul className="absolute bottom-full left-0 z-30 mb-1 max-h-48 w-full overflow-y-auto rounded border border-border bg-background shadow-md">
          {matches.map((c, i) => (
            <li key={`${c.kind}:${c.id}`}>
              <button
                type="button"
                // mousedown, not click: the textarea's blur fires first on a
                // click and would close the picker before the row was chosen.
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(c)
                }}
                className={`flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-[12px] ${
                  i === highlight ? 'bg-muted' : 'hover:bg-muted/60'
                }`}
              >
                {/* A session carries its mark (RFC 0311), the same one its
                    row draws, so you pick the one you recognise. */}
                <span className="flex min-w-0 items-center gap-1.5">
                  {c.kind === 'session' ? (
                    <SessionMark id={c.id} size={16} />
                  ) : null}
                  <span className="truncate text-foreground">{c.label}</span>
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground/60">
                  {c.hint ?? c.kind}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
